import { baseApi, API_TAGS, createCacheTags, createEndpointBuilder, type Params } from '@/shared/api';
import { handleFileDownloadResponse } from '@/shared/lib';

import {
  RECEIVE_ENDPOINT_SEGMENTS,
  receiveLocationShipmentsSegment,
  receiveShipmentBalancesSegment
} from '../config/constants';
import type {
  ReceiveExportParams,
  ReceiveLocationDto,
  ReceiveLocationShipmentsDto,
  ReceiveLocationShipmentsQueryArgs,
  ReceiveLocationsQueryArgs,
  ReceiveShipmentBalancesDto,
  ReceiveShipmentBalancesQueryArgs
} from '../model/types';

const receiveEndpoint = createEndpointBuilder('/api/v2/receive');

const provideReceiveTags = (args: object) =>
  createCacheTags({
    baseTag: API_TAGS.RECEIVE,
    params: args as unknown as Params
  });

/**
 * Receive Dashboard API v2. The hierarchy is fetched lazily, one level per
 * expand — `/locations` returns counts only, and each nested packet is requested
 * when its row opens:
 *
 *   page load     → getReceiveLocations       (one call per card)
 *   expand group  → getLocationShipments      (that location only)
 *   expand cons.  → getShipmentBalances       (that shipment only)
 */
export const receiveApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getReceiveLocations: builder.query<ReceiveLocationDto[], ReceiveLocationsQueryArgs>({
      query: (params) => ({
        url: receiveEndpoint(RECEIVE_ENDPOINT_SEGMENTS.LOCATIONS),
        params
      }),
      providesTags: (_result, _error, args) => provideReceiveTags(args)
    }),

    getLocationShipments: builder.query<ReceiveLocationShipmentsDto, ReceiveLocationShipmentsQueryArgs>({
      query: ({ locationId, ...params }) => ({
        url: receiveEndpoint(receiveLocationShipmentsSegment(locationId)),
        params
      }),
      providesTags: (_result, _error, args) => provideReceiveTags(args)
    }),

    getShipmentBalances: builder.query<ReceiveShipmentBalancesDto, ReceiveShipmentBalancesQueryArgs>({
      query: ({ shipmentId, ...params }) => ({
        url: receiveEndpoint(receiveShipmentBalancesSegment(shipmentId)),
        params
      }),
      providesTags: (_result, _error, args) => provideReceiveTags(args)
    }),

    exportReceive: builder.query<Blob, ReceiveExportParams>({
      query: (params) => ({
        url: receiveEndpoint(RECEIVE_ENDPOINT_SEGMENTS.EXPORT),
        params,
        responseHandler: (response) => handleFileDownloadResponse(response, `receive.${params.format || 'xlsx'}`),
        cache: 'no-cache'
      })
    })
  })
});

export const {
  useGetReceiveLocationsQuery,
  useGetLocationShipmentsQuery,
  useGetShipmentBalancesQuery,
  // Lazy variants back the expand-driven fetches — see `useLocationShipments` /
  // `useShipmentBalances`, which are what consumers should reach for.
  useLazyGetLocationShipmentsQuery,
  useLazyGetShipmentBalancesQuery,
  useLazyExportReceiveQuery
} = receiveApi;
